import Separator, {
	SeparatorType,
} from "@components/CustomTypography/Separator/Separator"
import ButtonComponent from "@components/Theme/Button/Button"
import Typography, {
	Size,
	Variant,
} from "@components/WebsiteRevamp/Typography/Typography"
import {getLink} from "@hooks/PagesData/getLink"
import Link from "next/link"
import React, {useEffect, useState} from "react"

import ErrorMessage from "../ErrorMessage/ErrorMessage"
import Input from "../Input/Input"
import ThankYou from "../ThankYou/ThankYou"
import {
	matchFieldRule,
	maxLengthRule,
	minLengthRule,
	prepareErrorResponseForCustomForm,
	requiredRule,
} from "../Validations"

import styles from "./ContactFree.module.scss"

const ContactFree = (props: {
	container?: SeparatorType
	button?: string
}): JSX.Element => {
	const [loading, setLoading] = useState(false)
	const [serverError, setServerError] = useState(false)
	const [firstname, setFirstname] = useState("")
	const [lastname, setLastname] = useState("")
	const [email, setEmail] = useState("")
	const [formSubmitted, setFormSubmitted] = useState(false)
	const [formSuccess, setFormSuccess] = useState(false)
	const [confirmEmail, setConfirmEmail] = useState("")

	const [errors, setErrors] = useState<{[key: string]: any}>({
		firstRender: true,
	})

	const handleError = (field: string, error: any) => {
		setErrors((prevErrors) => {
			const updated = {...prevErrors}
			delete updated.firstRender
			return {...updated, [field]: error}
		})
	}

	const handleDeleteError = (fieldName: string): void => {
		setErrors((prevErrors) => {
			const updatedErrors = Object.fromEntries(
				Object.entries(prevErrors).filter(([key]) => key !== fieldName),
			)
			delete updatedErrors.firstRender
			return updatedErrors
		})
	}

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		setFormSubmitted(true)
	}

	const sendData = async () => {
		try {
			setServerError(false)
			setLoading(true)

			const response = await fetch("/form/register-free-account", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					firstName: firstname,
					lastName: lastname,
					email,
				}),
			})

			if (!response.ok) {
				const errorData = await response.json()
				const errorsResponse =
					prepareErrorResponseForCustomForm(errorData)
				setErrors(errorsResponse.fields)
			} else {
				setFormSuccess(true)
			}
		} catch {
			setServerError(true)
		} finally {
			setLoading(false)
			setFormSubmitted(false)
		}
	}

	useEffect(() => {
		if (formSubmitted && Object.keys(errors).length === 0) {
			void sendData()
		} else {
			setFormSubmitted(false)
		}
	}, [formSubmitted])

	useEffect(() => {
		if (confirmEmail.length > 0 && confirmEmail !== email) {
			handleError("notConfirmEmail", "Must match Email")
		} else {
			handleDeleteError("notConfirmEmail")
		}
	}, [email])

	const container = {
		className: styles.doubleInput,
	}

	return (
		<Separator withoutMeasure {...props.container}>
			{!formSuccess && (
				<form
					className={styles.formContainer}
					onSubmit={handleSubmit}
					autoComplete="off">
					<Separator
						className={styles.doubleInputWrapper}
						type={"padding"}
						withoutMeasure>
						<Input
							container={container}
							disabled={loading}
							errors={errors}
							formSubmitted={formSubmitted}
							label={"First Name"}
							withFake
							name={"firstname"}
							rules={[
								requiredRule,
								minLengthRule(2),
								maxLengthRule(225),
							]}
							value={firstname}
							onChange={(e) => setFirstname(e.target.value)}
							onDeleteError={handleDeleteError}
							onError={handleError}
						/>
						<Input
							container={container}
							disabled={loading}
							errors={errors}
							formSubmitted={formSubmitted}
							withFake
							label={"Last Name"}
							name={"lastname"}
							rules={[
								requiredRule,
								minLengthRule(2),
								maxLengthRule(225),
							]}
							value={lastname}
							onChange={(e) => setLastname(e.target.value)}
							onDeleteError={handleDeleteError}
							onError={handleError}
						/>
					</Separator>

					<Input
						container={container}
						disabled={loading}
						errors={errors}
						formSubmitted={formSubmitted}
						label={"Email"}
						name={"notemail"}
						withFake
						rules={[
							requiredRule,
							minLengthRule(2),
							maxLengthRule(225),
						]}
						type={"email"}
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						onDeleteError={handleDeleteError}
						onError={handleError}
						inputProps={{
							autoComplete: "off",
						}}
					/>

					<Input
						container={container}
						disabled={loading}
						errors={errors}
						formSubmitted={formSubmitted}
						label={"Confirm Email"}
						name={"notConfirmEmail"}
						withFake
						rules={[
							requiredRule,
							matchFieldRule(() => email, "Email"),
						]}
						type={"email"}
						value={confirmEmail}
						onChange={(e) => setConfirmEmail(e.target.value)}
						onDeleteError={handleDeleteError}
						onError={handleError}
						inputProps={{
							autoComplete: "off",
						}}
					/>

					<Typography
						className={styles.privacyText}
						noPadding
						contrast
						variant={Variant.Description}>
						By signing up, you agree to our{" "}
						<Link
							href={"/one-free-edition-privacy-statement"}
							rel={"noreferrer"}
							style={{
								display: "inline-block",
								textDecoration: "underline",
							}}
							target={"_blank"}>
							privacy policies
						</Link>{" "}
						and{" "}
						<Link
							href={getLink("oneFreeEditionEula")}
							rel={"noreferrer"}
							style={{
								display: "inline-block",
								textDecoration: "underline",
							}}
							target={"_blank"}>
							terms of use
						</Link>{" "}
						and the handling of your personal information.
					</Typography>
					<Separator size={32} />
					<div className={styles.buttonContainer}>
						<ButtonComponent
							action={"submit"}
							buttonType={"primary"}
							className={styles.button}
							disabled={loading}
							size={"large"}>
							{loading
								? "Registering..."
								: props.button || "Sign up for free"}
						</ButtonComponent>
					</div>
					<Separator size={16} />

					<Typography
						variant={Variant.Description}
						size={Size.XXS}
						contrast
						content={
							"*The sign ups will be processed during business hours."
						}
					/>
				</form>
			)}

			{serverError && (
				<ErrorMessage
					errors={{
						server: "Something went wrong. Please try again later.",
					}}
					name={"server"}
				/>
			)}

			{formSuccess && (
				<ThankYou
					name={firstname}
					customeClassname={styles.thankYouContainer}
					customThankText={"Thank you for signing up, "}
					contrast
					customText={
						"Your free account sign up has now been registered successfully. The sign ups will be now processed during business hours and you can expect further information via email."
					}
				/>
			)}
		</Separator>
	)
}

export default ContactFree
